import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';

import { getLeaveHistory } from "src/services/leaveService"; 
import { RootStateType } from 'src/store/RootReducer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import '../NewDashboard.css';
import { LeaveStatus } from "../enum/eLeaveStatus";

const StatusIcons = {
  [LeaveStatus.APPROVED]: <CheckCircleIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: 'green' }} />,
  [LeaveStatus.APPROVED_PM_RM]: <ThumbUpIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: 'blue' }} />,
  [LeaveStatus.HR_REJECTED]: <ThumbDownIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: 'red' }} />,
  [LeaveStatus.RM_REJECTED]: <CancelIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: 'red' }} />,
  [LeaveStatus.PENDING]: <HourglassEmptyIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: 'orange' }} />,
  [LeaveStatus.CANCELLATION_REQUESTED]: <HourglassEmptyIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: '#9C27B0' }} />,
  [LeaveStatus.CANCELLATION_REJECTED]: <CancelIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: '#E65100' }} />,
  [LeaveStatus.CANCELLED]: <RemoveCircleIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: 'gray' }} />,
  [LeaveStatus.DEFAULT]: <AccessTimeIcon sx={{ alignSelf: 'center', fontSize: '1rem', paddingRight: '2px', color: 'black' }} />,
};

const formatStatus = (status: string, hrApproved?: boolean): LeaveStatus => {
  if (!status) return LeaveStatus.DEFAULT;

  switch (status) {
    case 'APPROVED':
    case 'HR_APPROVED':
    case 'RM_APPROVED':
      return hrApproved && status === 'RM_APPROVED' ? LeaveStatus.APPROVED_PM_RM : LeaveStatus.APPROVED;
    case 'HR_REJECTED':
      return LeaveStatus.HR_REJECTED;
    case 'RM_REJECTED':
      return LeaveStatus.RM_REJECTED;
    case 'PENDING':
      return LeaveStatus.PENDING;
    case 'CANCELLATION_REQUESTED':
      return LeaveStatus.CANCELLATION_REQUESTED;
    case 'CANCELLATION_REJECTED':
      return LeaveStatus.CANCELLATION_REJECTED;
    case 'CANCELLED':
      return LeaveStatus.CANCELLED;
    default:
      return LeaveStatus.DEFAULT;
  }
};

const getStatusIcon = (formattedStatus: LeaveStatus) => {
  return StatusIcons[formattedStatus] || StatusIcons[LeaveStatus.DEFAULT];
};

const LeavesWidget = () => {
  const navigate = useNavigate();
  const [leavesData, setLeavesData] = useState([]);
  const employeeId = useSelector(
    (state: RootStateType) => state.appReducer?.user?.EmployeeId
  );

  useEffect(() => {
    const fetchLeaveHistory = async () => {
      try {
        const response = await getLeaveHistory(employeeId); 
        const leaveData = response?.data?.data;
        
        if (Array.isArray(leaveData) && leaveData.length > 0) {
          const latestThreeLeaves = leaveData
            .sort((a, b) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime())
            .slice(0, 3);
          setLeavesData(latestThreeLeaves);
        }
      } catch (error) {
        toast.error("Failed to fetch leave history. Please try again later.");
      }
    };
    
    fetchLeaveHistory();
  }, [employeeId]);

  const handleShowMore = () => {
  
    navigate(`/leave-management/my-leave-history/id`);
  };

  return (
    <div className="leaves-widget">
      <div className="widget-header">
        <EventIcon
          sx={{ alignSelf: 'center', fontSize: '20px', marginRight: '6px' }}
        />
        My Latest Leaves
      </div>

      <div className="leaves-list">
        {leavesData.map((leave) => (
          <div key={leave.id} className="leave-item">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div className="leave-details">
                <span className="leave-type">{leave.typeName}</span>
              </div>
              <div className={`leave-status ${formatStatus(leave.status)}`}>
                {getStatusIcon(formatStatus(leave.status))}
                {formatStatus(leave.status)}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline'
              }}
            >
              <div>
                <span className="leave-date">{leave.fromDate} to {leave.toDate}</span>
              </div>
              <div className="leave-duration">
                (
                {leave.typeName === 'Short Leave'
                  ? '0.25 day'
                  : `${leave.daysCount} ${
                      leave.daysCount === 1 || leave.daysCount === 0.5
                        ? 'day'
                        : 'days'
                    }`}
                )
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="show-more">
        <button onClick={handleShowMore}>Show more →</button>
      </div>
    </div>
  );
};

export default LeavesWidget;